import * as React from 'react';

import { cn } from '@/lib/utils';

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  state?: 'default' | 'error';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /**
   * `md` (default) — sleek auth/marketing field. 44px below `lg:` breakpoint,
   * 48px from `lg:` (>=1024px) up. Tracks Button `lg`, Select, and MultiSelect
   * so every field on an auth screen shares the same vertical rhythm.
   * `sm` — dense admin-form field (py-2.5, font-normal value text).
   */
  size?: 'md' | 'sm';
}

// Always-wrapper architecture. Both the no-icon and the with-icon case render
// as `<div class="...flex items-center..."><input /></div>`. This guarantees
// the wrapper drives layout (so heights are byte-for-byte identical whether
// or not a `+91`/icon prefix is present) and removes the subpixel mismatch
// that came from native `<input>` intrinsic sizing diverging from a flex
// container measuring its child.
const fieldShellBase =
  'flex w-full items-center gap-3 rounded-[10px] border bg-surface px-4 transition-[border,box-shadow] disabled:cursor-not-allowed disabled:opacity-50';

const fieldShellSize: Record<NonNullable<InputProps['size']>, string> = {
  // Sleek auth/marketing field. 44px below lg keeps the form compact on
  // mobile/tablet where the form owns the viewport; 48px on lg+ gives the
  // input a bit more breathing room next to the desktop brand panel split.
  // Select, MultiSelect, and Button `lg` mirror this ramp so every field
  // and CTA on an auth screen sits on the same vertical rhythm at every
  // breakpoint.
  md: 'min-h-11 py-2.5 lg:min-h-12 lg:py-3',
  // Dense admin/workspace field — matches Select sm trigger so dense
  // tables (RangesEditor, CriticalItemsEditor, QuestionRow scale grid) sit
  // on a single visual baseline. Padding-based so the value text breathes
  // against the bold SemiMono labels above each field.
  sm: 'min-h-10 py-2.5',
};

const shellDefault =
  'border-primary-100 focus-within:border-primary-300 focus-within:shadow-(--shadow-focus-ring)';

const shellError =
  'border-error-300 focus-within:border-error-500 focus-within:shadow-(--shadow-focus-ring-error)';

const inputBase =
  'w-full flex-1 bg-transparent font-sans text-body-md outline-none autofill:bg-surface';

const inputSize: Record<NonNullable<InputProps['size']>, string> = {
  md: 'font-light',
  sm: 'font-normal',
};

// Placeholder leans on neutral-500 (#717a78) rather than -400 — neutral-400 reads
// as "disabled" against the white surface, especially on a tinted page bg.
// neutral-500 is still clearly secondary but actually legible at a glance.
const inputDefault = 'text-accent-ink placeholder:text-neutral-500 autofill:text-accent-ink';

const inputErrorText = 'text-error-500 placeholder:text-error-500 autofill:text-error-500';

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, state, startIcon, endIcon, size = 'md', ...props }, ref) => {
    const isError =
      state === 'error' || props['aria-invalid'] === true || props['aria-invalid'] === 'true';
    const shellState = isError ? shellError : shellDefault;
    const textState = isError ? inputErrorText : inputDefault;
    const affixTone = isError ? 'text-error-500' : 'text-primary-200';

    return (
      <div className={cn(fieldShellBase, fieldShellSize[size], shellState, className)}>
        {startIcon ? <span className={cn('shrink-0', affixTone)}>{startIcon}</span> : null}
        <input
          type={type}
          ref={ref}
          className={cn(inputBase, inputSize[size], textState)}
          {...props}
        />
        {endIcon ? <span className={cn('shrink-0', affixTone)}>{endIcon}</span> : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
